import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, ShieldCheck, AlertCircle, TrainFront, CheckCircle2, Loader2 } from "lucide-react";
import { useDtmAuth } from "../../contexts/DtmAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function DtmAcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { applySession } = useDtmAuth();

  const [checking, setChecking] = useState(true);
  const [invite, setInvite] = useState(null);
  const [invalid, setInvalid] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setInvalid("This invite link is missing its token."); setChecking(false); return; }
    axios.get(`${API}/interview-coaching/coach/invite/${token}`)
      .then((r) => {
        if (r.data.already_accepted) { setInvalid("This invite has already been accepted. Please sign in instead."); }
        else { setInvite(r.data); setName(r.data.name || ""); }
      })
      .catch((e) => setInvalid(e?.response?.data?.detail || "This invite link is invalid or has expired."))
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setErr("Passwords do not match."); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/interview-coaching/coach/accept-invite`, {
        token, password, name: name.trim(),
      });
      applySession(res.data.token);
      toast.success("Welcome aboard! Your coach portal is ready.");
      navigate("/dtm", { replace: true });
    } catch (er) {
      setErr(er?.response?.data?.detail || "Could not accept invite.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1B2E] flex items-center justify-center p-4" data-testid="dtm-accept-invite-page">
      <div className="max-w-md w-full">
        <div className="flex items-center gap-3 justify-center mb-6">
          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
            <TrainFront className="w-6 h-6 text-[#0F1B2E]" strokeWidth={2.4} />
          </div>
          <div className="text-white">
            <p className="font-extrabold leading-none">Interview Coach</p>
            <p className="text-[11px] uppercase tracking-widest text-slate-400">Train Driver Foundation</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-7">
          {checking ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-7 h-7 text-[#E63B2E] animate-spin" /></div>
          ) : invalid ? (
            <div className="text-center space-y-3">
              <div className="flex items-center gap-2 text-rose-700 justify-center"><AlertCircle className="w-5 h-5" /><h1 className="tdf-heading text-lg text-tdf-navy">Invite unavailable</h1></div>
              <p className="text-sm text-slate-500" data-testid="dtm-invite-invalid">{invalid}</p>
              <button onClick={() => navigate("/dtm/login")} className="btn-pill btn-primary text-sm">Go to sign in</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="flex items-center gap-2 text-[#E63B2E]"><ShieldCheck className="w-5 h-5" /><h1 className="tdf-heading text-xl text-tdf-navy">Accept your invite</h1></div>
              <p className="text-sm text-slate-500">
                You've been invited as <strong className="text-tdf-navy">{invite.email}</strong>. Set a password to create your coach account.
              </p>
              <div>
                <label className="block text-sm font-bold text-tdf-navy mb-1.5">Your name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} data-testid="dtm-invite-name"
                  className="w-full rounded-lg border border-slate-300 focus:border-[#E63B2E] focus:ring-2 focus:ring-[#E63B2E]/20 outline-none px-4 py-2.5" />
              </div>
              <div>
                <label className="block text-sm font-bold text-tdf-navy mb-1.5">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="dtm-invite-password"
                    className="w-full rounded-lg border border-slate-300 focus:border-[#E63B2E] focus:ring-2 focus:ring-[#E63B2E]/20 outline-none pl-10 pr-4 py-2.5"
                    placeholder="At least 8 characters" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-tdf-navy mb-1.5">Confirm password</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="dtm-invite-confirm"
                    className="w-full rounded-lg border border-slate-300 focus:border-[#E63B2E] focus:ring-2 focus:ring-[#E63B2E]/20 outline-none pl-10 pr-4 py-2.5" />
                </div>
              </div>
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 text-xs">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                After signing up you'll read and sign the coaching documents. Our team will then approve your account before you can take bookings.
              </div>
              {err && (
                <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-800 rounded-lg p-3 text-sm" data-testid="dtm-invite-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {err}
                </div>
              )}
              <button type="submit" disabled={saving} data-testid="dtm-invite-submit" className="w-full btn-pill btn-primary disabled:opacity-60">
                {saving ? "Creating account…" : "Create my coach account"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
